import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import debounce from 'lodash/debounce';
import numeral from 'numeral';
import { analytics } from 'libs';
import Filter from './Filter';
import Text from './Text';

interface BeaconFilterProps {
  filters: { beacon?: number | string };
  isPro?: boolean;
  onFilter: (update: { beacon: number | null }) => void;
  placeholder?: string;
}

const BeaconFilter = ({ filters, isPro = false, onFilter, placeholder = 'Enter beacon score' }: BeaconFilterProps) => {
  const [value, setValue] = useState(filters.beacon);

  /**
   * The first render will occur before the initial filter state is ready. If
   * the filter state was ready on first render, the state sync below would
   * be unnecessary. To remove this, we would need the parent component to
   * immediately pass down the actual initial filter state.
   */
  useEffect(() => {
    setValue(filters.beacon);
  }, [filters.beacon, setValue]);

  const onDebouncedFilter = useMemo(
    () =>
      debounce((newFilters) => {
        onFilter(newFilters);
      }, 400),
    [onFilter]
  );

  useEffect(() => () => onDebouncedFilter.cancel(), [onDebouncedFilter]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();

    const newValue = numeral(event.target.value).value();

    analytics.productSearch.filterDebounced({ beacon: newValue || 'cleared' });

    setValue(newValue as any);
    onDebouncedFilter({ beacon: newValue });
  };

  return (
    <Filter name="Beacon" isPro={isPro}>
      <Text
        isPro={isPro}
        onChange={onChange}
        placeholder={placeholder}
        testId="filter-beacon"
        value={value ? numeral(value).format() : ''}
      />
    </Filter>
  );
};

export default BeaconFilter;
